/**
 * Billing query + mutation layer. Reads key under `["venue", venueId, "billing", …]` so the shell's
 * `eventQueryKeyMap` (`billing.subscription.*` → `["venue", venueId, "billing"]`) invalidates them when a
 * subscription changes (dunning, reactivation). Invoices + card live in the Stripe Billing Portal now.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { billingApi, type BillingProfile, type PlanCatalog, type Subscription } from "./billingApi";

export const billingKeys = {
  plans: () => ["billing", "plans"] as const,
  profile: () => ["billing", "profile"] as const,
  subscription: (venueId: string) => ["venue", venueId, "billing", "subscription"] as const,
};

export function usePlanCatalog() {
  return useQuery<PlanCatalog>({
    queryKey: billingKeys.plans(),
    queryFn: ({ signal }) => billingApi.plans(signal),
    staleTime: 300_000,
  });
}

export function useVenueSubscription(venueId: string, enabled = true) {
  return useQuery<Subscription | null>({
    queryKey: billingKeys.subscription(venueId),
    queryFn: ({ signal }) => billingApi.subscription(venueId, signal),
    staleTime: 15_000,
    // The subscription endpoint is account-root only (403 otherwise); callers that render for non-root
    // users (e.g. the shell UpgradeBanner) pass enabled=false to avoid firing a doomed request.
    enabled,
  });
}

export function useBillingProfile() {
  return useQuery<BillingProfile>({
    queryKey: billingKeys.profile(),
    queryFn: ({ signal }) => billingApi.getProfile(signal),
    staleTime: 60_000,
  });
}

export function useSaveBillingProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (profile: BillingProfile) => billingApi.putProfile(profile),
    onSuccess: (saved) => qc.setQueryData(billingKeys.profile(), saved),
  });
}

/** Start Stripe Checkout for a plan — Stripe collects the card + creates the sub. Redirects on success. */
export function useStartCheckout(venueId: string) {
  return useMutation({
    mutationFn: (vars: { planCode: string; addOnCodes: string[] }) =>
      billingApi.checkout(
        venueId,
        vars.planCode,
        vars.addOnCodes,
        window.location.href,
        window.location.href,
      ),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });
}

/**
 * Open the Stripe Billing Portal (card + invoice/receipt history) in a NEW TAB. Popup blockers only allow
 * `window.open` inside a user gesture, but our portal URL comes from an async round-trip — so the caller
 * opens a blank tab SYNCHRONOUSLY in the click handler and passes the handle here; we then point it at the
 * returned url once it resolves (mirrors the venue-site preview fix). A null handle (blocked) falls back to
 * a best-effort `window.open`.
 */
export function useOpenBillingPortal() {
  return useMutation({
    mutationFn: (_target: Window | null) => billingApi.portalSession(window.location.href),
    onSuccess: ({ url }, target) => {
      if (target && !target.closed) target.location.href = url;
      else window.open(url, "_blank");
    },
    onError: (_err, target) => {
      target?.close();
    },
  });
}

export function useChangePlan(venueId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { planCode: string; addOnCodes: string[] }) =>
      billingApi.changePlan(venueId, vars.planCode, vars.addOnCodes),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: billingKeys.subscription(venueId) });
    },
  });
}

/** Schedule cancel-at-period-end (in-app): the sub stays ACTIVE until the period end, then CANCELLED. */
export function useCancelSubscription(venueId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => billingApi.cancelSubscription(venueId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: billingKeys.subscription(venueId) });
    },
  });
}

/** Undo a scheduled cancel — resume the subscription. */
export function useReactivateSubscription(venueId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => billingApi.reactivateSubscription(venueId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: billingKeys.subscription(venueId) });
    },
  });
}
